<template>
  <div v-if="product">
    <ProductSheetBase :product="product" :bigPicture="true">
      <template v-slot:pictures>
        <PictureViewer :pictures="pictures" />
        <div class="consult text-uppercase">{{ $t('product_sheet.consult') }}</div>
      </template>

      <template v-slot:product-info>
        <div v-if="price" class="mt-4">
          <b-link :to="productPharmaciesRoute" class="pharmacies">
            <SVGProductPharmacies class="pr-2" />
            {{ $t("product_sheet.pharmacies") }}
          </b-link>
        </div>
      </template>

      <template v-slot:price>
        <div class="price">{{ $formatMoney(price, currency) }}</div>
        <div v-if="discount" class="mb-2">
          <div
            v-if="discountPackQuantity"
            class="discount-type"
          >({{ $t("product_sheet.pack_offer") }} x{{ discountPackQuantity }})</div>
          <span class="no-discount-price mr-2">{{ $formatMoney(priceBefore, currency) }}</span>
          <span class="discount-box px-2 py-1">-{{ discountPercent }}%</span>
        </div>
        <div class="items">{{ $t('product_sheet.items', { nItems: 50 }) }}</div>
        <div class="pharmacy-name">{{ pharmacyName }}</div>
      </template>

      <template v-slot:purchase-info>
        <div class="best-prices my-4 pt-3">
          <div class="best-prices-title mb-3">{{ $t("product_sheet.best_prices") }}</div>
          <ProductPriceList :product="product" :maxPrices="2" />
        </div>

        <div class="conditions mt-4 mb-2 pt-3 pb-2">
          <div>
            <font-awesome-icon icon="calendar-alt" class="mr-1"></font-awesome-icon>
            {{ $t('product_sheet.receive') }}
          </div>
          <div>
            <font-awesome-icon icon="lock" class="mr-1"></font-awesome-icon>
            {{ $t('product_sheet.payment') }}
          </div>
        </div>
        <div class="mail">
          <font-awesome-icon icon="envelope" class="mr-1"></font-awesome-icon>
          <a :href="getMailUrlShare()">{{ $t('product_sheet.send') }}</a>
        </div>
      </template>
    </ProductSheetBase>
    <div v-if="product.attributes && product.attributes.length > 0" class="my-4">
      <button
        v-b-toggle.collapse-attributes
        class="product-attributes btn p-0"
        v-bind:class="{ 'visible-attributes': showAttributes }"
      >
        <SVGProductInfo class="pr-2" />
        {{ $t("product_sheet.product_info") }}
      </button>
    </div>
    <b-collapse v-if="product.attributes" id="collapse-attributes" v-model="showAttributes">
      <ProductAttributes :product="product" />
    </b-collapse>
  </div>
</template>


<script>
import { ProductCardMixin } from "@/mixins/ProductCardMixin";
import ProductSheetBase from "@/components/ProductSheetBase";
import PictureViewer from "@/components/PictureViewer";
import ProductPriceList from "@/components/ProductPriceList";
import ProductAttributes from "@/components/ProductAttributes";

import SVGProductPharmacies from "@/static/product_pharmacies.svg";
import SVGProductInfo from "@/static/product_info.svg";

export default {
  name: "product-sheet",
  components: {
    ProductSheetBase,
    PictureViewer,
    ProductPriceList,
    ProductAttributes,
    SVGProductPharmacies,
    SVGProductInfo,
  },
  mixins: [ProductCardMixin],
  props: {
    product: Object,
  },
  data() {
    return {
      quantity: Number(1),
      showAttributes: false,
    };
  },
  computed: {
    pictures() {
      if (this.product.drug_pictures.length > 0) {
        return this.product.drug_pictures;
      } else {
        return [require("@/static/fallback_drug_picture.png")];
      }
    },
    productPharmaciesRoute() {
      return {
        name: "product_pharmacies",
        params: {
          drugUuid: this.$route.params.drugUuid,
        },
      };
    },
  },
  methods: {
    getMailUrlShare() {
      return (
        "mailto:?subject=" +
        this.product.default_description +
        "&body=" +
        this.getAbsoluteUrl()
      );
    },
    getTwitterUrlShare() {
      return (
        "https://twitter.com/intent/tweet?text=" +
        this.product.default_description +
        " " +
        this.getAbsoluteUrl()
      );
    },
    getFacebookUrlShare() {
      return (
        "https://www.facebook.com/sharer/sharer.php?u=" + this.getAbsoluteUrl()
      );
    },
    getAbsoluteUrl() {
      return window.location.origin.concat(window.location.pathname);
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        product_sheet: {
          consult: "Consult your physician",
          items: "Only {nItems} items at this price",
          free_shipment: "FREE shipment from 49€ by pharmacy",
          receive: "Receive at home in 2 to 5 days",
          payment: "100% safe payment",
          return: "Guaranteed return within 14 days",
          pharmacies: "Search for pharmacies that sell this product",
          product_info: "See product info",
          send: "Send to a friend",
          pack_offer: "Pack Offer",
          best_prices: "Best published prices",
        },
      },
    },
  },
};
</script>


<style lang="scss" scoped>
.consult {
  color: $text-fourth-color;
  font-size: $font-size-small;
  font-weight: bold;
}

.price {
  color: $corp-color2;
  font-size: $font-size-medium * 2;
  font-weight: bold;
}

.items {
  color: $corp-color1;
  font-size: $font-size-medium;
}

.best-prices {
  border-top: 1px solid $border-color;
}

.best-prices-title {
  color: $corp-color1;
  font-weight: bold;
}

.conditions {
  border-top: 1px solid $border-color;
  border-bottom: 1px solid $border-color;
  line-height: $font-size-medium * 1.5;
}

.pharmacies {
  color: $corp-color1;
  font-size: $font-size-medium * 1.2;
  font-weight: bold;
  text-decoration: underline;

  svg {
    fill: $corp-color1;
    stroke: $corp-color1;
    width: 4rem;
  }
}

.product-attributes {
  color: $text-main-color;
  font-size: $font-size-medium * 1.2;
  font-weight: bold;
  text-decoration: underline;

  &:hover {
    color: $text-main-color;
    text-decoration: underline;
  }

  &:focus {
    box-shadow: none;
  }

  &.visible-attributes {
    color: $text-fourth-color;

    svg {
      fill: $text-fourth-color;
      stroke: $text-fourth-color;
    }
  }

  svg {
    fill: $text-main-color;
    stroke: $text-main-color;
    width: 4rem;
  }
}

.mail a {
  color: $text-main-color;
  text-decoration: none;
}

.discount-type {
  color: $grey5-color;
}

.no-discount-price {
  color: $grey5-color;
  font-size: $font-size-medium * 1.1;
  font-weight: bold;
  text-decoration: line-through;
}

.discount-box {
  background-color: $discount-bg-color;
  color: $white;
  font-size: $font-size-medium * 1.1;
  font-weight: bold;
}

.pharmacy-name {
  font-size: $font-size-small;
}
</style>
